import { cva, type VariantProps } from 'class-variance-authority'
import {
  CircleAlert,
  CircleCheckBig,
  Info,
  Lock,
  CircleOff,
  LucideIcon
} from 'lucide-react'
import { HTMLAttributes, Ref, FC } from 'react'

import { cn } from '../utils/cn'

export const calloutVariants = cva(
  'rounded-2xl flex gap-4 items-center border',
  {
    variants: {
      calloutType: {
        error: 'bg-red-100 border-red-300',
        info: 'bg-blue-100 border-blue-300',
        success: 'bg-green-100 border-green-300',
        warning: 'bg-amber-100 border-amber-300',
        lock: 'bg-amber-100 border-amber-300',
        disabled: 'bg-amber-100 border-amber-300'
      },
      calloutSize: {
        sm: 'px-4 py-2 gap-2 rounded-lg',
        base: 'p-4 rounded-2xl gap-4'
      },
      color: {
        red: 'bg-red-100 border-red-300',
        green: 'bg-green-100 border-green-300',
        blue: 'bg-blue-100 border-blue-300',
        amber: 'bg-amber-100 border-amber-300',
        gray: 'bg-gray-100 border-gray-300'
      }
    }
  }
)

const calloutIconVariants = cva('shrink-0', {
  variants: {
    calloutType: {
      error: 'bg-red-500',
      success: 'bg-green-500',
      info: 'bg-blue-600',
      warning: 'bg-amber-500',
      lock: 'bg-amber-500',
      disabled: 'bg-amber-500'
    },
    calloutSize: {
      sm: 'bg-transparent size-4',
      base: 'p-2 rounded-full size-9 text-white'
    },
    color: {
      red: 'bg-red-500',
      green: 'bg-green-500',
      blue: 'bg-blue-600',
      amber: 'bg-amber-500',
      gray: 'bg-gray-500'
    }
  },
  compoundVariants: [
    {
      calloutType: 'error',
      calloutSize: 'sm',
      className: 'text-red-500'
    },
    {
      calloutType: 'success',
      calloutSize: 'sm',
      className: 'text-green-500'
    },
    {
      calloutType: 'info',
      calloutSize: 'sm',
      className: 'text-blue-600'
    },
    {
      calloutType: 'warning',
      calloutSize: 'sm',
      className: 'text-amber-500'
    },
    {
      calloutType: 'lock',
      calloutSize: 'sm',
      className: 'text-amber-500'
    },
    {
      calloutType: 'disabled',
      calloutSize: 'sm',
      className: 'text-amber-500'
    }
  ]
})

const calloutTextVariants = cva('font-light', {
  variants: {
    calloutSize: {
      sm: 'text-sm',
      base: 'text-base'
    }
  },
  defaultVariants: {
    calloutSize: 'base'
  }
})

interface CalloutProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof calloutVariants> {
  ref?: Ref<HTMLDivElement>
  icon?: LucideIcon
  color?: 'red' | 'green' | 'blue' | 'amber' | 'gray'
  hideIcon?: boolean
}

const ICONS_MAP = {
  error: CircleAlert,
  warning: CircleAlert,
  success: CircleCheckBig,
  info: Info,
  lock: Lock,
  disabled: CircleOff
}

const CALLOUT_LABELS: Record<keyof typeof ICONS_MAP, string> = {
  error: 'Error',
  warning: 'Warning',
  success: 'Success',
  info: 'Information',
  lock: 'Locked',
  disabled: 'Disabled'
}

const CALLOUT_ROLES: Partial<Record<keyof typeof ICONS_MAP, string>> = {
  error: 'alert'
}

interface CalloutText
  extends HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof calloutTextVariants> {}

export const CalloutText: FC<CalloutText> = ({
  children,
  calloutSize,
  className
}) => {
  return (
    <p className={cn(calloutTextVariants({ calloutSize }), className)}>
      {children}
    </p>
  )
}

export const Callout: FC<CalloutProps> = ({
  ref,
  className,
  calloutType,
  calloutSize = 'base',
  icon,
  hideIcon,
  color,
  children,
  ...props
}) => {
  const resolvedType = calloutType ?? 'info'
  const DefaultIcon = ICONS_MAP[resolvedType]
  const IconComponent = icon || DefaultIcon
  const variants = color
    ? { color, calloutSize }
    : { calloutType: resolvedType, calloutSize }
  const iconClassName = calloutIconVariants(variants)

  const label = calloutType ? CALLOUT_LABELS[calloutType] : undefined
  const role = calloutType ? CALLOUT_ROLES[calloutType] : undefined

  return (
    <div
      ref={ref}
      {...(role ? { role } : {})}
      {...props}
      className={cn(
        calloutVariants({
          ...(color ? { color } : { calloutType: resolvedType }),
          calloutSize
        }),
        className
      )}>
      {!hideIcon && <IconComponent aria-hidden className={iconClassName} />}
      {label && <span className="sr-only">{label}</span>}
      {children}
    </div>
  )
}
